import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { attorneys } from '../../mocks/attorneys';

const AttorneyProfilePage = () => {
  const { id } = useParams();
  const attorney = attorneys.find((a) => a.id === Number(id));

  if (!attorney) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-serif text-4xl text-navy-900 mb-4">Avukat Bulunamadı</h1>
          <Link to="/attorneys" className="text-gold-500 hover:text-gold-600 cursor-pointer">
            Avukatlar Sayfasına Dön
          </Link>
        </div>
      </div>
    );
  }

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-navy-900">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link
              to="/attorneys"
              className="inline-flex items-center text-gold-500 hover:text-gold-400 mb-8 cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              <span className="font-sans text-sm">Tüm Avukatlara Dön</span>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Profile Section */}
      <section className="py-16 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-1"
            >
              <div className="sticky top-32">
                <div className="relative overflow-hidden rounded-lg border-2 border-gold-500 mb-6">
                  <img
                    src={attorney.image}
                    alt={attorney.name}
                    className="w-full h-auto object-cover"
                  />
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h1 className="font-serif text-3xl text-navy-900 mb-2">{attorney.name}</h1>
                  <p className="font-sans text-lg text-gold-500 mb-6">{attorney.title}</p>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex items-start">
                      <i className="ri-briefcase-line text-gold-500 mr-3 mt-1"></i>
                      <div>
                        <div className="font-sans text-sm font-semibold text-navy-900">Uzmanlık</div>
                        <div className="font-sans text-sm text-gray-600">{attorney.specialization}</div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <i className="ri-time-line text-gold-500 mr-3 mt-1"></i>
                      <div>
                        <div className="font-sans text-sm font-semibold text-navy-900">Deneyim</div>
                        <div className="font-sans text-sm text-gray-600">{attorney.experience}</div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <i className="ri-scales-3-line text-gold-500 mr-3 mt-1"></i>
                      <div>
                        <div className="font-sans text-sm font-semibold text-navy-900">Baro Üyeliği</div>
                        <div className="font-sans text-sm text-gray-600">{attorney.barMembership}</div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <i className="ri-global-line text-gold-500 mr-3 mt-1"></i>
                      <div>
                        <div className="font-sans text-sm font-semibold text-navy-900">Diller</div>
                        <div className="font-sans text-sm text-gray-600">{attorney.languages}</div>
                      </div>
                    </div>
                  </div>

                  <Link
                    to="/contact"
                    className="block w-full px-6 py-3 bg-gold-500 text-white text-center font-sans text-sm font-medium rounded-md hover:bg-gold-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Danışmanlık Talep Edin
                  </Link>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-2"
            >
              <div className="space-y-12">
                <div>
                  <h2 className="font-serif text-3xl text-navy-900 mb-6">Biyografi</h2>
                  <p className="font-sans text-base text-gray-700 leading-relaxed">{attorney.bio}</p>
                </div>

                <div>
                  <h2 className="font-serif text-3xl text-navy-900 mb-6">Eğitim</h2>
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <p className="font-sans text-base text-gray-700 leading-relaxed">{attorney.education}</p>
                  </div>
                </div>

                <div>
                  <h2 className="font-serif text-3xl text-navy-900 mb-6">Uzmanlık Alanları</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {attorney.practiceAreas.map((area, index) => (
                      <div
                        key={index}
                        className="flex items-center p-4 bg-gray-50 rounded-lg"
                      >
                        <i className="ri-checkbox-circle-fill text-gold-500 mr-3"></i>
                        <span className="font-sans text-sm text-gray-700">{area}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h2 className="font-serif text-3xl text-navy-900 mb-6">Başarılar ve Tanınma</h2>
                  <div className="space-y-3">
                    {attorney.achievements.map((achievement, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-star-fill text-gold-500 mr-3 mt-1"></i>
                        <p className="font-sans text-base text-gray-700">{achievement}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Other Attorneys */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="font-serif text-4xl text-navy-900 mb-4">Diğer Avukatlarımız</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {attorneys
              .filter((a) => a.id !== attorney.id)
              .slice(0, 3)
              .map((otherAttorney) => (
                <Link
                  key={otherAttorney.id}
                  to={`/attorney-profile/${otherAttorney.id}`}
                  className="group block cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-lg border border-gold-500/30 mb-4">
                    <img
                      src={otherAttorney.image}
                      alt={otherAttorney.name}
                      className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="bg-white p-6 rounded-lg">
                    <h3 className="font-sans text-xl font-semibold text-navy-900 mb-1">{otherAttorney.name}</h3>
                    <p className="font-sans text-sm text-gray-600 mb-3">{otherAttorney.title}</p>
                    <div className="inline-flex items-center text-gold-500 text-sm font-medium group-hover:translate-x-2 transition-transform">
                      <span>Profili Görüntüle</span>
                      <i className="ri-arrow-right-line ml-2"></i>
                    </div>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AttorneyProfilePage;